import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getTutorialToken, setTutorialToken, tutorialAPI } from '@/utils/tutorialApi';

const TutorialAuthContext = createContext(null);

export function TutorialAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = getTutorialToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const data = await tutorialAPI.getMe();
      setUser(data || null);
    } catch {
      setTutorialToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (username, password) => {
    const data = await tutorialAPI.login(username, password);
    if (data?.token) {
      setTutorialToken(data.token);
      setUser(data.user || null);
    }
    return data;
  }, []);

  const register = useCallback(async (username, password) => {
    const data = await tutorialAPI.register(username, password);
    if (data?.token) {
      setTutorialToken(data.token);
      setUser(data.user || null);
    }
    return data;
  }, []);

  const applyEditor = useCallback(async (reason) => {
    const data = await tutorialAPI.applyEditor(reason);
    if (data?.user) {
      setUser(data.user);
    }
    return data;
  }, []);

  const updateProfile = useCallback(async (payload) => {
    const data = await tutorialAPI.updateProfile(payload);
    if (data?.token) {
      setTutorialToken(data.token);
    }
    if (data?.user) {
      setUser(data.user);
    }
    return data;
  }, []);

  const logout = useCallback(() => {
    setTutorialToken(null);
    setUser(null);
  }, []);

  const value = useMemo(() => ({
    user,
    loading,
    isAuthenticated: Boolean(user),
    isEditor: Boolean(user?.isEditor),
    isManager: Boolean(user?.isManager),
    editorApplyStatus: user?.editorApplyStatus || 'NONE',
    login,
    register,
    applyEditor,
    updateProfile,
    logout,
    refresh,
  }), [user, loading, login, register, applyEditor, updateProfile, logout, refresh]);

  return (
    <TutorialAuthContext.Provider value={value}>
      {children}
    </TutorialAuthContext.Provider>
  );
}

export function useTutorialAuth() {
  const ctx = useContext(TutorialAuthContext);
  if (!ctx) {
    throw new Error('useTutorialAuth must be used within TutorialAuthProvider');
  }
  return ctx;
}
